/* External */
import { combineReducers } from "redux";
import { call, put, select, takeLatest } from "redux-saga/effects";
import camelCaseL from "lodash/camelCase";
import cloneDeepL from "lodash/cloneDeep";
import upperFirstL from "lodash/upperFirst";

/* Others */
import { STATUS_TYPES } from "../utils";

const ACTION_TYPES_VARIATIONS = {
  FAILURE: "FAILURE",
  FETCH: "FETCH",
  RESET: "RESET",
  SUCCESS: "SUCCESS"
};

/**
 * Constructs and returns an action name based on a base action name and a variation of the action
 * @param {string} baseActionName base name for the action generated
 * @param {string} variation The variation of the action
 * @returns {string} The new action name
 */
const makeActionName = (baseActionName, variation) => `${baseActionName}/${variation}`;

/**
 * Generates a set of action types based on a base action name
 * @param {string} baseActionName base name for the actions to be generated
 * @returns {object} Object with the action types created: FETCH, RESET, SUCCESS; FAILURE
 */
export const makeFetchActionTypes = (baseActionName) => ({
  FAILURE: makeActionName(baseActionName, ACTION_TYPES_VARIATIONS.FAILURE),
  FETCH: makeActionName(baseActionName, ACTION_TYPES_VARIATIONS.FETCH),
  RESET: makeActionName(baseActionName, ACTION_TYPES_VARIATIONS.RESET),
  SUCCESS: makeActionName(baseActionName, ACTION_TYPES_VARIATIONS.SUCCESS)
});

/**
 * Generates a set of action creators related with fetch processes
 * @param {string} baseActionName base name for the actions generated
 * @returns {object} object with keys: `fetch`, `success`, `failure`, and `reset`
 * Each is a action creator for the respective actionType
 */
export const makeFetchActionCreators = (baseActionTypesName) => {
  const actionTypes = makeFetchActionTypes(baseActionTypesName);

  return {
    fetch: (payload) => ({
      type: actionTypes.FETCH,
      payload
    }),
    success: (data, originalPayload) => ({
      type: actionTypes.SUCCESS,
      payload: { data, originalPayload }
    }),
    failure: (error, originalPayload) => ({
      type: actionTypes.FAILURE,
      payload: { error, originalPayload }
    }),
    reset: () => ({
      type: actionTypes.RESET
    })
  };
};

export const FETCH_REDUCERS_KEYS = {
  DATA: "data",
  STATUS: "status",
  ERROR: "error"
};

/**
 * This function creates reducers data, status and error for actions related with a base action name.
 * The reducer controls SUCCESS, FETCH and FAILURE variations of the action.
 * @param {string} baseActionName base name of the action for the reducers to be created
 * @param {any} defaultDataState initial state for the data reducer created
 * @returns {object} Object that has to keys: data, status and error. Each one is a reducer.
 * data is the reducer for data received in payload.
 * status is the reducer for the status of the fetch.
 * error is the reducer when an error occurs while fetching
 */
export const makeFetchReducers = (baseActionName, defaultDataState = null, options = {}) => {
  const {
    accumulateResults,
    additionalResetAction,
    reducersToCombine = {},
    whitelistedKeys,
    customReset = null
  } = options;
  const actionTypes = makeFetchActionTypes(baseActionName);

  const reducers = {
    [FETCH_REDUCERS_KEYS.DATA]: (state = defaultDataState, action) => {
      switch (action.type) {
        case actionTypes.SUCCESS:
          const { data } = action.payload;

          if (accumulateResults) {
            let newState;

            if (state) {
              newState = cloneDeepL(state);

              newState.push(data);
            } else {
              newState = [data];
            }

            return newState;
          }

          return data || state;
        case actionTypes.RESET:
        case additionalResetAction:
          if (customReset) {
            const resetState = customReset(state);
            return resetState;
          }
          return defaultDataState;
        default:
          return state;
      }
    },
    [FETCH_REDUCERS_KEYS.STATUS]: (state = STATUS_TYPES.UNLOAD_STATE, action) => {
      switch (action.type) {
        case actionTypes.FETCH:
          return STATUS_TYPES.INIT_STATE;
        case actionTypes.SUCCESS:
          return STATUS_TYPES.SUCCEED_STATE;
        case actionTypes.FAILURE:
          return STATUS_TYPES.FAILED_STATE;
        case actionTypes.RESET:
        case additionalResetAction:
          return STATUS_TYPES.UNLOAD_STATE;
        default:
          return state;
      }
    },
    [FETCH_REDUCERS_KEYS.ERROR]: (state = null, action) => {
      switch (action.type) {
        case actionTypes.FAILURE:
          return action.payload.error;
        case actionTypes.RESET:
        case actionTypes.SUCCESS:
        case actionTypes.FETCH:
        case additionalResetAction:
          return null;
        default:
          return state;
      }
    }
  };

  if (whitelistedKeys) {
    Object.keys(reducers).forEach((key) => {
      if (!whitelistedKeys.includes(key)) delete reducers[key];
    });
  }

  return combineReducers({
    ...reducersToCombine,
    ...reducers
  });
};

/**
 * Creates a saga effect for the FETCH variation of an action. The saga effect takeLatest FETCH variation
 * then make the request, and the puts the SUCCESS or FAILURE variation.
 * @param {string} baseActionName Base name of the action which this saga is related to
 * @param {object|function} fetchFunction A function to fetch data for the action or an object with keys `func`
 * and `getParams`. Both are functions. `func` is the function to be called to fetch data and `getParams` is called
 * with the payload for the fetch action and the current state as params and should return an array containing the
 * params to be passed to `func`.
 * @param {function} processResponseFunction A function to process data after fetched and before
 * pass to reducer as payload of SUCCESS action.
 * @param {function} takeFunction An effect function provided by redux-saga to know when should be the saga executed.
 * Defaults to `takeLatest`
 * @returns The saga effect created (To be yielded).
 */
export const makeFetchSaga = (
  baseActionName,
  fetchFunction,
  processResponseFunction = (response) => response,
  takeFunction = takeLatest,
  onError
) => {
  const actionTypes = makeFetchActionTypes(baseActionName);
  const actionCreators = makeFetchActionCreators(baseActionName);

  return takeFunction(actionTypes.FETCH, function* ({ payload } = {}) {
    try {
      const fetchFunctionType = typeof fetchFunction;
      let func, params;

      if (fetchFunctionType === "function") {
        func = fetchFunction;
        params = [payload];
      } else if (fetchFunctionType === "object") {
        const state = yield select();

        func = fetchFunction.getFunc ? fetchFunction.getFunc(payload) : fetchFunction.func;
        params = fetchFunction.getParams(payload, state);
      } else {
        throw new Error("Invalid fetch function");
      }

      const response = yield call(func, ...params);
      const processedResponse = yield call(processResponseFunction, response?.data, payload);

      yield put(actionCreators.success(processedResponse, payload));
    } catch (error) {
      // next code is just debug
      const splittedBaseActionName = baseActionName.split("/");
      const entityName = upperFirstL(camelCaseL(splittedBaseActionName[splittedBaseActionName.length - 1]));
      const generalSagaName = splittedBaseActionName.map((part) => camelCaseL(part)).join("/");

      console.error(`${generalSagaName}Saga-fetch${entityName}Saga:`, error, error?.response);
      // END debug

      if (onError) {
        yield call(onError, error, payload);
      } else {
        yield put(actionCreators.failure(error, payload));
      }
    }
  });
};

export function createBaseReducer(actionTypes, name, initialStateValues) {
  const initialState = {
    status: STATUS_TYPES.UNLOAD_STATE,
    ...initialStateValues
  };
  return function (state = initialState, { type, payload }) {
    switch (type) {
      case actionTypes.REQUEST:
        return { ...state, status: STATUS_TYPES.INIT_STATE };
      case actionTypes.SUCCESS:
        return {
          ...state,
          status: STATUS_TYPES.SUCCEED_STATE,
          [name]: payload,
          error: initialState.error || null
        };
      case actionTypes.FAILURE:
        return {
          ...state,
          status: STATUS_TYPES.FAILED_STATE,
          error: payload,
          [name]: initialState[name] || null
        };
      case actionTypes.RESET:
        return initialState;
      default:
        return state;
    }
  };
}
